import React, { useState, useEffect } from 'react';
import { API_URL } from "../config";
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { 
  FaCheckCircle, FaTimes, FaEye, FaClock,
  FaTruck, FaMapMarkerAlt,
  FaCalendarAlt
} from 'react-icons/fa';
import SubbieLayout from '../layouts/SubbieLayout';

function SubbieNewJobs({ onLogout }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [requirements, setRequirements] = useState([]);
  const [subbieId, setSubbieId] = useState(null);
  const [showAcceptModal, setShowAcceptModal] = useState(false);
  const [selectedRequirement, setSelectedRequirement] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewingRequirement, setViewingRequirement] = useState(null);
  const [showDeclineModal, setShowDeclineModal] = useState(false);
  const [declineReason, setDeclineReason] = useState('');

  const user = JSON.parse(localStorage.getItem('user') || '{}');

  useEffect(() => {
    const getSubbieId = async () => {
      try {
        let savedId = localStorage.getItem('subbieId');
        if (savedId) { setSubbieId(savedId); fetchData(savedId); return; }
        const response = await fetch(`${API_URL}/subcontractors?user_id=${user.id}`);
        const data = await response.json();
        if (data.success && data.subcontractors && data.subcontractors.length > 0) {
          const id = data.subcontractors[0].id;
          setSubbieId(id);
          localStorage.setItem('subbieId', id);
          fetchData(id);
        } else { setError('No subcontractor profile found'); setLoading(false); }
      } catch (err) { console.error('Error fetching subbie ID:', err); setError('Failed to load profile'); setLoading(false); }
    };
    if (user.id) getSubbieId(); else { setError('User not logged in'); setLoading(false); }
  }, []);

  const fetchData = async (id) => {
    setLoading(true); setError(null);
    try {
      const response = await fetch(`${API_URL}/subbie/all-requirements/${id}`);
      const data = await response.json();
      if (data.success) {
        const pendingReqs = data.requirements.filter(r => {
          if (r.status === 'pending' || r.status === 'quoting') return true;
          if (r.jobs?.status === 'new') return true;
          if (r.status === 'approved' || r.status === 'accepted') return true;
          return false;
        });
        setRequirements(pendingReqs);
      } else { setError(data.message || 'Failed to load data'); }
    } catch (err) { console.error('Error fetching data:', err); setError('Failed to load jobs'); }
    finally { setLoading(false); }
  };

  // ── Simplified Accept — just accept the job, no quoting ──
  const handleAcceptJob = async (scheduleNow = false) => {
    if (!selectedRequirement) return;
    setProcessing(true);
    try {
      const response = await fetch(`${API_URL}/subbie/accept-job`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          job_requirement_id: selectedRequirement.id,
          accept_rate: true
        })
      });
      const data = await response.json();
      if (data.success) {
        toast.success('✅ Job accepted successfully!');
        setShowAcceptModal(false);
        if (scheduleNow) {
          // Navigate to scheduler
          navigate(`/subbie-scheduler/${selectedRequirement.job_id}`);
        }
        fetchData(subbieId);
      } else {
        toast.error(data.error || 'Failed to accept job');
      }
    } catch (err) { console.error('Error accepting job:', err); toast.error('Failed to accept job'); }
    finally { setProcessing(false); }
  };

  // ── Decline with reason ──
  const handleDeclineJob = async () => {
    if (!selectedRequirement) return;
    setProcessing(true);
    try {
      const response = await fetch(`${API_URL}/subbie/accept-job`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          job_requirement_id: selectedRequirement.id,
          accept_rate: false,
          decline_reason: declineReason || 'No reason provided'
        })
      });
      const data = await response.json();
      if (data.success) {
        toast.success('Job declined — admin has been notified');
        setShowDeclineModal(false);
        setDeclineReason('');
        fetchData(subbieId);
      } else {
        toast.error(data.error || 'Failed to decline');
      }
    } catch (err) { console.error('Error declining:', err); toast.error('Failed to decline'); }
    finally { setProcessing(false); }
  };

  const handleAcceptClick = (req) => { setSelectedRequirement(req); setShowAcceptModal(true); };
  const handleDeclineClick = (req) => { setSelectedRequirement(req); setShowDeclineModal(true); setDeclineReason(''); };
  const handleViewJob = (req) => { setViewingRequirement(req); setShowViewModal(true); };

  const getStatusBadge = (status) => {
    const styles = {
      'pending': 'bg-yellow-100 text-yellow-600',
      'quoting': 'bg-blue-100 text-blue-600',
      'approved': 'bg-green-100 text-green-600',
      'accepted': 'bg-blue-100 text-blue-600',
      'pending_approval': 'bg-purple-100 text-purple-600',
    };
    return `px-2 py-1 rounded-full text-xs font-semibold ${styles[status] || 'bg-gray-100 text-gray-600'}`;
  };

  const getStatusLabel = (status) => {
    const labels = {
      'pending': 'Awaiting Response',
      'quoting': 'Quote Submitted',
      'approved': 'Approved - Accept Job',
      'accepted': 'Accepted',
      'pending_approval': 'Pending Admin Approval',
    };
    return labels[status] || status;
  };

  // Mirror the admin New Jobs card look.
  const getQuoteTypeLabel = (type) => {
    const labels = { minerals: '⛏️ Minerals', agri: '🌾 Agri', abnormal: '🚛 Abnormal', container: '📦 Containers', general: '📋 General' };
    return labels[type] || type;
  };
  const jobTypeBorderColors = {
    minerals: 'border-l-red-400', agri: 'border-l-green-400', abnormal: 'border-l-purple-400',
    container: 'border-l-yellow-400', general: 'border-l-blue-400'
  };
  const jobTypeBadgeColors = {
    minerals: 'bg-red-100 text-red-700', agri: 'bg-green-100 text-green-700', abnormal: 'bg-purple-100 text-purple-700',
    container: 'bg-yellow-100 text-yellow-700', general: 'bg-blue-100 text-blue-700'
  };
  const getPriorityLabel = (p) => ({ low: 'LOW', normal: 'NORMAL', high: 'HIGH', urgent: 'URGENT' }[p?.toLowerCase()] || 'NORMAL');
  const getPriorityColor = (p) => ({
    low: 'bg-gray-100 text-gray-600', normal: 'bg-blue-100 text-blue-600',
    high: 'bg-orange-100 text-orange-700', urgent: 'bg-red-100 text-red-700'
  }[p?.toLowerCase()] || 'bg-blue-100 text-blue-600');

  if (loading) {
    return (
      <SubbieLayout activeTab="newjobs" onLogout={onLogout} user={user}>
        <div style={{display:'flex', alignItems:'center', justifyContent:'center', height:'256px'}}>
          <div style={{textAlign:'center'}}><div style={{fontSize:'48px', marginBottom:'16px'}}>🔄</div><p style={{color:'#6b7280'}}>Loading jobs...</p></div>
        </div>
      </SubbieLayout>
    );
  }

  if (error) {
    return (
      <SubbieLayout activeTab="newjobs" onLogout={onLogout} user={user}>
        <div style={{backgroundColor:'white', borderRadius:'12px', padding:'32px', textAlign:'center', border:'1px solid #e5e7eb', maxWidth:'448px', margin:'0 auto'}}>
          <div style={{fontSize:'48px', marginBottom:'16px'}}>⚠️</div>
          <h2 style={{fontSize:'20px', fontWeight:'bold', color:'#dc2626', marginBottom:'8px'}}>Error Loading Jobs</h2>
          <p style={{color:'#6b7280'}}>{error}</p>
          <button onClick={() => fetchData(subbieId)} style={{marginTop:'16px', backgroundColor:'#1a2a3a', color:'white', padding:'8px 24px', borderRadius:'8px', border:'none', cursor:'pointer'}}>🔄 Retry</button>
        </div>
      </SubbieLayout>
    );
  }

  return (
    <SubbieLayout activeTab="newjobs" onLogout={onLogout} user={user}>
      <div style={{width:'100%'}}>
        <h2 style={{fontSize:'24px', fontWeight:'bold', color:'#1a2a3a', marginBottom:'24px', display:'flex', alignItems:'center', gap:'12px'}}>
          <FaTruck style={{color:'#3b82f6'}} /> New Jobs
        </h2>
        {requirements.length === 0 ? (
          <div style={{backgroundColor:'white', borderRadius:'12px', padding:'48px', textAlign:'center', border:'1px solid #e5e7eb', boxShadow:'0 1px 3px rgba(0,0,0,.06)'}}>
            <div style={{fontSize:'64px', marginBottom:'16px'}}>📋</div>
            <h3 style={{fontSize:'20px', fontWeight:'600', color:'#1a2a3a', marginBottom:'8px'}}>No New Jobs Available</h3>
            <p style={{color:'#6b7280', marginBottom:'16px'}}>You'll be notified when new jobs are assigned to you.</p>
            <button onClick={() => fetchData(subbieId)} style={{backgroundColor:'#1a2a3a', color:'white', padding:'8px 24px', borderRadius:'8px', border:'none', cursor:'pointer'}}>🔄 Refresh</button>
          </div>
        ) : (
          <div style={{display:'flex', flexDirection:'column', gap:'16px'}}>
            {requirements.map(req => {
              const job = req.jobs || {};
              const jobType = job.quote_type || 'minerals';
              const canRespond = req.status === 'pending';
              const hasResponded = req.status === 'quoting' || req.status === 'approved' || req.status === 'accepted';
              const isApproved = req.status === 'approved';
              const origin = req.origin || job.origin || 'N/A';
              const destination = req.destination || job.destination || 'N/A';

              return (
                <div key={req.id} className={`bg-white rounded-xl border border-gray-200 border-l-4 ${jobTypeBorderColors[jobType] || 'border-l-gray-200'} p-4 mb-3 shadow-sm hover:shadow-md transition-shadow`}>
                  <div className="flex justify-between items-start gap-3">
                    <div className="flex-1 min-w-0">
                      {/* Header: number + badges — NO client name (privacy) */}
                      <div className="flex items-center flex-wrap gap-2 mb-2">
                        <h3 className="text-lg font-bold text-navy">{job.job_number || 'Job'}</h3>
                        <span className={`px-2 py-1 rounded-full text-xs font-semibold ${jobTypeBadgeColors[jobType] || 'bg-blue-100 text-blue-600'}`}>
                          {getQuoteTypeLabel(jobType)}
                        </span>
                        <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getPriorityColor(job.priority)}`}>
                          {getPriorityLabel(job.priority)}
                        </span>
                        <span className={getStatusBadge(req.status)}>{getStatusLabel(req.status)}</span>
                      </div>

                      {/* Managed job + trucks required */}
                      <div className="flex items-center gap-2 text-sm text-gray-600 mb-2">
                        <FaTruck className="text-green-500" />
                        <span>Via One Purpose Logistics · <strong>{req.trucks_required || 1}</strong> truck{(req.trucks_required || 1) > 1 ? 's' : ''} required</span>
                      </div>

                      {/* Route — full addresses live on the requirement */}
                      <div className="flex flex-wrap items-center gap-3 text-sm text-gray-500 mb-2">
                        <span className="flex items-center gap-1">
                          <FaMapMarkerAlt size={14} className="text-amber-500" />
                          {origin} → {destination}
                        </span>
                        {job.cross_border === true && (
                          <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full font-medium">🛂 Cross-Border</span>
                        )}
                      </div>

                      {/* Dates */}
                      <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500 mb-1">
                        <span className="flex items-center gap-1"><FaCalendarAlt size={14} /> Start: {job.start_date || 'TBD'}</span>
                        <span className="flex items-center gap-1"><FaClock size={14} /> Offered: {req.created_at ? new Date(req.created_at).toLocaleDateString('en-ZA', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}</span>
                      </div>

                      {isApproved && (
                        <div className="mt-2 flex items-center gap-2 bg-green-50 border border-green-200 text-green-700 rounded-lg px-3 py-2 text-sm">
                          <FaCheckCircle /> Admin approved — Ready to accept!
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="flex gap-3 mt-4 flex-wrap">
                    <button onClick={() => handleViewJob(req)} className="flex items-center gap-1.5 px-4 py-2 bg-gray-100 text-gray-700 border border-gray-200 rounded-lg text-sm font-medium hover:bg-gray-200 transition-colors">
                      <FaEye size={14} /> View Details
                    </button>
                    {canRespond && (
                      <>
                        <button onClick={() => handleAcceptClick(req)} className="flex items-center gap-1.5 px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-semibold hover:bg-green-700 transition-colors">
                          <FaCheckCircle size={14} /> Accept
                        </button>
                        <button onClick={() => handleDeclineClick(req)} className="flex items-center gap-1.5 px-4 py-2 bg-white text-red-600 border border-red-500 rounded-lg text-sm font-medium hover:bg-red-50 transition-colors">
                          <FaTimes size={14} /> Decline
                        </button>
                      </>
                    )}
                    {hasResponded && (
                      <span className="flex items-center gap-1.5 px-4 py-2 bg-green-50 text-green-700 rounded-lg text-sm font-medium">
                        <FaCheckCircle size={14} /> {req.status === 'accepted' ? 'Accepted ✓' : 'Response Submitted'}
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ═══ ACCEPT MODAL — Schedule Now / Schedule Later ═══ */}
      {showAcceptModal && selectedRequirement && (
        <div style={{position:'fixed', inset:0, zIndex:50, display:'flex', alignItems:'center', justifyContent:'center'}}>
          <div style={{position:'fixed', inset:0, background:'rgba(0,0,0,0.5)'}} onClick={() => setShowAcceptModal(false)}></div>
          <div style={{position:'relative', background:'white', borderRadius:'16px', padding:'28px', maxWidth:'480px', width:'90%', boxShadow:'0 20px 60px rgba(0,0,0,.2)'}} onClick={e => e.stopPropagation()}>
            <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'20px'}}>
              <h3 style={{margin:0, fontSize:'18px', fontWeight:'bold', color:'#1a2a3a'}}>Accept Job</h3>
              <button onClick={() => setShowAcceptModal(false)} style={{border:'none', background:'none', fontSize:'22px', color:'#9ca3af', cursor:'pointer'}}>✕</button>
            </div>
            <p style={{fontSize:'14px', color:'#6b7280', marginBottom:'20px'}}>
              <strong>{selectedRequirement.jobs?.job_number}</strong> — managed by One Purpose Logistics<br/>
              {(() => {
                const j = selectedRequirement.jobs || {};
                if (j.subbie_target_price > 0) {
                  return <>Your rate: <strong>R{Number(j.subbie_target_price).toFixed(2)}/MT</strong></>;
                }
                const li = (Array.isArray(j.line_items) && j.line_items.length)
                  ? j.line_items
                  : (Array.isArray(j.items) ? j.items : []);
                const t = li.reduce((s, x) => s + (Number(x.cost_price || x.rate) || 0), 0);
                return t > 0
                  ? <>Rate: <strong style={{color:'#059669'}}>R{t.toFixed(2)}</strong></>
                  : <>Your rate: <strong>R0.00/MT</strong></>;
              })()}
            </p>
            <div style={{display:'flex', flexDirection:'column', gap:'10px'}}>
              <button
                onClick={() => handleAcceptJob(true)}
                disabled={processing}
                style={{width:'100%', padding:'14px 18px', backgroundColor:'#059669', color:'#fff', border:'none', borderRadius:'10px', cursor:'pointer', fontWeight:'600', fontSize:'14px', display:'flex', alignItems:'center', justifyContent:'center', gap:'8px'}}
              >
                <FaCalendarAlt size={16} /> Accept & Schedule Now
              </button>
              <p style={{margin:'0', fontSize:'11px', color:'#9ca3af', textAlign:'center'}}>Accept the job and go directly to the fleet scheduler.</p>
              <button
                onClick={() => handleAcceptJob(false)}
                disabled={processing}
                style={{width:'100%', padding:'14px 18px', backgroundColor:'#1a2a3a', color:'#fff', border:'none', borderRadius:'10px', cursor:'pointer', fontWeight:'600', fontSize:'14px', display:'flex', alignItems:'center', justifyContent:'center', gap:'8px'}}
              >
                <FaClock size={16} /> Accept & Schedule Later
              </button>
              <p style={{margin:'0', fontSize:'11px', color:'#9ca3af', textAlign:'center'}}>Accept now and schedule fleet when you're ready. You'll get reminder notifications.</p>
            </div>
          </div>
        </div>
      )}

      {/* ═══ DECLINE MODAL ═══ */}
      {showDeclineModal && selectedRequirement && (
        <div style={{position:'fixed', inset:0, zIndex:50, display:'flex', alignItems:'center', justifyContent:'center'}}>
          <div style={{position:'fixed', inset:0, background:'rgba(0,0,0,0.5)'}} onClick={() => setShowDeclineModal(false)}></div>
          <div style={{position:'relative', background:'white', borderRadius:'16px', padding:'28px', maxWidth:'460px', width:'90%', boxShadow:'0 20px 60px rgba(0,0,0,.2)'}} onClick={e => e.stopPropagation()}>
            <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'20px'}}>
              <h3 style={{margin:0, fontSize:'18px', fontWeight:'bold', color:'#dc2626'}}>Decline Job</h3>
              <button onClick={() => setShowDeclineModal(false)} style={{border:'none', background:'none', fontSize:'22px', color:'#9ca3af', cursor:'pointer'}}>✕</button>
            </div>
            <p style={{fontSize:'14px', color:'#6b7280', marginBottom:'16px'}}>
              <strong>{selectedRequirement.jobs?.job_number}</strong><br/>
              Let the admin know why you can't take this job.
            </p>
            <textarea
              value={declineReason}
              onChange={e => setDeclineReason(e.target.value)}
              placeholder="e.g. Trucks fully booked that week, can't handle that commodity..."
              rows={3}
              style={{width:'100%', padding:'12px', borderRadius:'8px', border:'1px solid #e5e7eb', fontSize:'14px', outline:'none', resize:'vertical', marginBottom:'16px', boxSizing:'border-box'}}
            />
            <div style={{display:'flex', gap:'10px', justifyContent:'flex-end'}}>
              <button onClick={() => setShowDeclineModal(false)} style={{padding:'10px 22px', backgroundColor:'#f3f4f6', color:'#374151', border:'1px solid #e5e7eb', borderRadius:'8px', cursor:'pointer', fontWeight:'500', fontSize:'14px'}}>Cancel</button>
              <button onClick={handleDeclineJob} disabled={processing} style={{padding:'10px 22px', backgroundColor:'#dc2626', color:'#fff', border:'none', borderRadius:'8px', cursor:'pointer', fontWeight:'600', fontSize:'14px', opacity: processing ? 0.5 : 1}}>
                {processing ? 'Declining...' : 'Decline Job'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ═══ VIEW DETAILS MODAL ═══ */}
      {showViewModal && viewingRequirement && (
        <div style={{position:'fixed', inset:0, zIndex:50, display:'flex', alignItems:'center', justifyContent:'center'}}>
          <div style={{position:'fixed', inset:0, background:'rgba(0,0,0,0.5)'}} onClick={() => setShowViewModal(false)}></div>
          <div style={{position:'relative', background:'white', borderRadius:'16px', padding:'28px', maxWidth:'560px', width:'90%', maxHeight:'90vh', overflow:'auto', boxShadow:'0 20px 60px rgba(0,0,0,.2)'}} onClick={e => e.stopPropagation()}>
            <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'16px', paddingBottom:'12px', borderBottom:'1px solid #e5e7eb'}}>
              <h3 style={{margin:0, fontSize:'18px', fontWeight:'bold', color:'#1a2a3a'}}>{viewingRequirement.jobs?.job_number}</h3>
              <button onClick={() => setShowViewModal(false)} style={{border:'none', background:'none', fontSize:'22px', color:'#9ca3af', cursor:'pointer'}}>✕</button>
            </div>
            <div style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:'12px', fontSize:'14px'}}>
              <div><p style={{color:'#6b7280', margin:0, fontSize:'12px'}}>Status</p><span className={getStatusBadge(viewingRequirement.status)} style={{display:'inline-block', marginTop:'4px'}}>{getStatusLabel(viewingRequirement.status)}</span></div>
              <div><p style={{color:'#6b7280', margin:0, fontSize:'12px'}}>Job Type</p><p style={{fontWeight:'500', color:'#1a2a3a', margin:'4px 0'}}>{getQuoteTypeLabel(viewingRequirement.jobs?.quote_type || 'minerals')}</p></div>
              <div><p style={{color:'#6b7280', margin:0, fontSize:'12px'}}>Origin</p><p style={{fontWeight:'500', color:'#1a2a3a', margin:'4px 0'}}>{viewingRequirement.origin || viewingRequirement.jobs?.origin || 'N/A'}</p></div>
              <div><p style={{color:'#6b7280', margin:0, fontSize:'12px'}}>Destination</p><p style={{fontWeight:'500', color:'#1a2a3a', margin:'4px 0'}}>{viewingRequirement.destination || viewingRequirement.jobs?.destination || 'N/A'}</p></div>
              <div><p style={{color:'#6b7280', margin:0, fontSize:'12px'}}>Start Date</p><p style={{fontWeight:'500', color:'#1a2a3a', margin:'4px 0'}}>{viewingRequirement.jobs?.start_date || 'TBD'}</p></div>
              <div><p style={{color:'#6b7280', margin:0, fontSize:'12px'}}>Offered</p><p style={{fontWeight:'500', color:'#1a2a3a', margin:'4px 0'}}>{viewingRequirement.created_at ? new Date(viewingRequirement.created_at).toLocaleDateString('en-ZA', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}</p></div>
              {viewingRequirement.jobs?.quote_type === 'abnormal' && viewingRequirement.jobs?.job_description && (<div><p style={{color:'#6b7280', margin:0, fontSize:'12px'}}>Description</p><p style={{fontWeight:'500', color:'#1a2a3a', margin:'4px 0', whiteSpace:'pre-line'}}>{viewingRequirement.jobs.job_description}</p></div>)}
              {viewingRequirement.jobs?.commodity && viewingRequirement.jobs?.quote_type !== 'abnormal' && (<div><p style={{color:'#6b7280', margin:0, fontSize:'12px'}}>Commodity</p><p style={{fontWeight:'500', color:'#1a2a3a', margin:'4px 0', whiteSpace:'pre-line'}}>{viewingRequirement.jobs.commodity}</p></div>)}
              {viewingRequirement.jobs?.total_quantity_mt > 0 && (<div><p style={{color:'#6b7280', margin:0, fontSize:'12px'}}>Tonnage</p><p style={{fontWeight:'500', color:'#1a2a3a', margin:'4px 0'}}>{viewingRequirement.jobs.total_quantity_mt} MT</p></div>)}
              {viewingRequirement.jobs?.quote_type === 'container' && viewingRequirement.jobs?.container_size && (<div><p style={{color:'#6b7280', margin:0, fontSize:'12px'}}>Container</p><p style={{fontWeight:'500', color:'#1a2a3a', margin:'4px 0'}}>{viewingRequirement.jobs.container_count || 1} × {viewingRequirement.jobs.container_size}{viewingRequirement.jobs.container_type ? ` ${viewingRequirement.jobs.container_type}` : ''}{viewingRequirement.jobs.is_empty ? ' (empty)' : ' (loaded)'}</p></div>)}
              {viewingRequirement.jobs?.combined_weight_t > 0 && (<div><p style={{color:'#6b7280', margin:0, fontSize:'12px'}}>Combined Weight</p><p style={{fontWeight:'500', color:'#1a2a3a', margin:'4px 0'}}>{viewingRequirement.jobs.combined_weight_t} t</p></div>)}
              {viewingRequirement.jobs?.truck_type && viewingRequirement.jobs.truck_type !== 'side_tipper' && (<div><p style={{color:'#6b7280', margin:0, fontSize:'12px'}}>Truck Type</p><p style={{fontWeight:'500', color:'#1a2a3a', margin:'4px 0'}}>{String(viewingRequirement.jobs.truck_type).replace(/_/g, ' ')}</p></div>)}
              {viewingRequirement.jobs?.cross_border && (<div><p style={{color:'#6b7280', margin:0, fontSize:'12px'}}>Cross-Border</p><p style={{fontWeight:'500', color:'#1a2a3a', margin:'4px 0'}}>Yes</p></div>)}
            </div>

            {(() => {
              const li = (Array.isArray(viewingRequirement.jobs?.line_items) && viewingRequirement.jobs.line_items.length)
                ? viewingRequirement.jobs.line_items
                : (Array.isArray(viewingRequirement.jobs?.items) && viewingRequirement.jobs.items.length ? viewingRequirement.jobs.items : []);
              return li.length > 0 ? (
                <div style={{marginTop:'16px'}}>
                  <p style={{color:'#6b7280', margin:'0 0 6px', fontSize:'12px'}}>Line Items (your cost)</p>
                  <div style={{display:'flex', flexDirection:'column', gap:'6px'}}>
                    {li.map((x, i) => (
                      <div key={x.id || i} style={{border:'1px solid #e5e7eb', borderRadius:'8px', padding:'8px 10px'}}>
                        <div style={{display:'flex', justifyContent:'space-between', gap:'8px'}}>
                          <p style={{fontWeight:'500', color:'#1a2a3a', margin:0, fontSize:'13px'}}>{x.title || x.description || x.commodity || 'Line item'}</p>
                          {(x.cost_price || x.rate || 0) > 0 && <p style={{fontWeight:'600', color:'#059669', margin:0, fontSize:'13px', flexShrink:0}}>R{Number(x.cost_price || x.rate || 0).toFixed(2)}</p>}
                        </div>
                        <p style={{color:'#6b7280', margin:'2px 0 0', fontSize:'12px'}}>
                          {x.quantity || 1} × {x.unit || 'load'}
                          {(x.pickup_location || x.delivery_location) ? ` · ${x.pickup_location || '?'} → ${x.delivery_location || '?'}` : ''}
                        </p>
                        {(x.pickup_date || x.delivery_date) && (
                          <p style={{color:'#0a6cff', margin:'2px 0 0', fontSize:'12px', fontWeight:'500'}}>
                            🗓 Pickup: {x.pickup_date ? new Date(x.pickup_date + 'T00:00:00').toLocaleDateString('en-ZA', { day: '2-digit', month: 'short' }) : 'TBC'}
                            {' · '}Delivery: {x.delivery_date ? new Date(x.delivery_date + 'T00:00:00').toLocaleDateString('en-ZA', { day: '2-digit', month: 'short' }) : 'TBC'}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                  {(() => {
                    const t = li.reduce((s, x) => s + (Number(x.cost_price || x.rate) || 0), 0);
                    return t > 0 ? (
                      <div style={{display:'flex', justifyContent:'space-between', marginTop:'10px', paddingTop:'10px', borderTop:'1px solid #e5e7eb'}}>
                        <span style={{color:'#6b7280', fontSize:'13px', fontWeight:'500'}}>Rate</span>
                        <span style={{fontWeight:'700', color:'#059669', fontSize:'14px'}}>R{t.toFixed(2)}</span>
                      </div>
                    ) : null;
                  })()}
                </div>
              ) : null;
            })()}

            {viewingRequirement.jobs?.notes && (<div style={{marginTop:'16px', padding:'12px', backgroundColor:'#f9fafb', borderRadius:'8px', fontSize:'14px', color:'#374151'}}><strong>Notes:</strong> {viewingRequirement.jobs.notes}</div>)}
          </div>
        </div>
      )}
    </SubbieLayout>
  );
}

export default SubbieNewJobs;